<template>
  <tabs />
  <toolbar class="toolbar">
    <filter-pane v-model:page="page" v-model="filter" :filters="filters" />
    <add-fields-pane v-model="visibleColumns" :fields="columns" />
    <export
      url="/history_usage_statistics/csv"
      :filter="filter"
      :columns="columns"
      :visible-columns="visibleColumns"
      :title="$t('csv.title.history_usage_statistics')"
      :sort-key="sortKey"
      :sort-direction="sortDirection"
    />
  </toolbar>
  <div class="ml-12">
    <div
      class="ml-12 flex items-center justify-end"
      style="justify-content: flex-end; padding: 1rem 2rem 1rem 1rem;"
    >
      <Switch
        v-model="showDiagram"
        :label="$t('pages.history.showDiagram')"
      />
    </div>
    <HistoryChart
      v-if="showDiagram"
      data-type="usage"
      :visible-columns="visibleColumns"
      :provider-params="providerParams"
    />
    <HistoryUsageDifferenceStatisticsTable
      v-if="!showDiagram"
      :provider-params="differenceProviderParams"
    />
    <div class="table">
      <data-table
        v-model:sort-by="sortKey"
        v-model:sort-direction="sortDirection"
        class="mb-auto"
        :busy="loadingTable"
        :items="records"
        :columns="columns"
        :visible-columns="visibleColumns"
      />
    </div>
  </div>
  <pagination
    v-model:page="page"
    v-model:per-page="perPage"
    class="pagination"
    :total="total"
    :per-page-choices="[5, 10, 50, 100, 1000, 10000]"
  />
</template>

<script lang="ts" setup>
import {
  computed, reactive, ref, unref,
} from 'vue';
import { format } from 'date-fns';
import { useI18n } from 'vue-i18n';
import FilterPane from '@utils/toolbar/panes/Filter.vue';
import AddFieldsPane from '@utils/toolbar/panes/AddFields.vue';
import Export from '@utils/toolbar/panes/Export.vue';
import Toolbar from '@utils/toolbar/Toolbar.vue';
import Pagination from '@utils/table/Pagination.vue';
import DataTable, { type Column } from '@utils/table/Table.vue';
import { useProvider } from '@utils/table/hooks/provider';
import type { Filter } from '@utils/filter/Filter.vue';
import Tabs from '@components/structure/navigation/Tabs.vue';
import Switch from '@utils/inputs/Switch.vue';
import HistoryChart from '@utils/chart/HistoryChart.vue';
import HistoryUsageDifferenceStatisticsTable from '@utils/HistoryUsageDifferenceStatisticsTable.vue';
import { useTableFields } from '@hooks/table-configuration';
import { useQueryRef } from '@/hooks/url';
import type { HistoryUsageStatistic, HydraCollection } from '@/entities/Entities';

const page = useQueryRef<number>('page', 1);
const perPage = useQueryRef<number>('perPage', 10);
const filter = useQueryRef<any>('filter', {});
const sortKey = useQueryRef<string | undefined>('sortKey', 'weekStartUtc');
const sortDirection = useQueryRef<'ASC' | 'DESC' | undefined>('sortDirection', 'DESC');
const showDiagram = ref(false);

const { t } = useI18n();

const providerParams = reactive({
  sort: {
    sortKey,
    sortDirection,
  },
  filter,
  pagination: {
    page,
    perPage,
  },
});

const differenceProviderParams = computed(() => ({
  filter: filter.value,
}));

type ColumnFilter = Partial<Filter> & Omit<Filter, 'key'>;
type ColumnDefinition = Column & { filter?: ColumnFilter | ColumnFilter[] } & { group: string };

const columns: ColumnDefinition[] = [
  {
    key: 'id',
    title: t('id'),
    sortable: false,
    visible: false,
    group: t('pages.history.fieldGroups.general'),
  },
  {
    key: 'source',
    title: t('pages.history.usageSource'),
    sortable: true,
    visible: false,
    group: t('pages.history.fieldGroups.general'),
    filter: {
      type: 'text',
      group: t('pages.history.filterGroups.general'),
      options: {
        label: t('pages.history.usageSource'),
      },
    },
  },
  {
    key: 'weekStartUtc',
    title: t('pages.history.weekStartUtc'),
    sortable: true,
    visible: true,
    formatter: (v: string) => (v ? format(new Date(v), 'dd.MM.yyyy - HH:mm:ss') : '-'),
    group: t('pages.history.fieldGroups.general'),
    filter: {
      type: 'datetime',
      group: t('pages.history.filterGroups.general'),
      options: {
        label: t('pages.history.weekStartUtc'),
      },
    },
  },
  {
    key: 'weekEndUtc',
    title: t('pages.history.weekEndUtc'),
    sortable: true,
    visible: true,
    formatter: (v: string) => (v ? format(new Date(v), 'dd.MM.yyyy - HH:mm:ss') : '-'),
    group: t('pages.history.fieldGroups.general'),
    filter: {
      type: 'datetime',
      group: t('pages.history.filterGroups.general'),
      options: {
        label: t('pages.history.weekEndUtc'),
      },
    },
  },
  {
    key: 'calendarYear',
    title: t('pages.history.calendarYear'),
    sortable: true,
    visible: true,
    group: t('pages.history.fieldGroups.general'),
    filter: {
      type: 'range',
      group: t('pages.history.filterGroups.general'),
      options: {
        label: t('pages.history.calendarYear'),
      },
    },
  },
  {
    key: 'calendarWeek',
    title: t('pages.history.calendarWeek'),
    sortable: true,
    visible: true,
    group: t('pages.history.fieldGroups.general'),
    filter: {
      type: 'range',
      group: t('pages.history.filterGroups.general'),
      options: {
        label: t('pages.history.calendarWeek'),
      },
    },
  },
  {
    key: 'distinctUsers',
    title: t('pages.history.distinctUsers'),
    sortable: true,
    visible: true,
    group: t('pages.history.fieldGroups.general'),
    filter: {
      type: 'range',
      group: t('pages.history.filterGroups.general'),
      options: {
        label: t('pages.history.distinctUsers'),
      },
    },
  },
  {
    key: 'uniqueUsersLastDay',
    title: t('pages.history.uniqueUsersLastDay'),
    sortable: true,
    visible: true,
    group: t('pages.history.fieldGroups.general'),
    filter: {
      type: 'range',
      group: t('pages.history.filterGroups.general'),
      options: {
        label: t('pages.history.uniqueUsersLastDay'),
      },
    },
  },
  {
    key: 'uniqueUsersLast7Days',
    title: t('pages.history.uniqueUsersLast7Days'),
    sortable: true,
    visible: true,
    group: t('pages.history.fieldGroups.general'),
    filter: {
      type: 'range',
      group: t('pages.history.filterGroups.general'),
      options: {
        label: t('pages.history.uniqueUsersLast7Days'),
      },
    },
  },
  {
    key: 'uniqueUsersLast30Days',
    title: t('pages.history.uniqueUsersLast30Days'),
    sortable: true,
    visible: true,
    group: t('pages.history.fieldGroups.general'),
    filter: {
      type: 'range',
      group: t('pages.history.filterGroups.general'),
      options: {
        label: t('pages.history.uniqueUsersLast30Days'),
      },
    },
  },
  {
    key: 'totalRequests',
    title: t('pages.history.totalRequests'),
    sortable: true,
    visible: true,
    group: t('pages.history.fieldGroups.general'),
    filter: {
      type: 'range',
      group: t('pages.history.filterGroups.general'),
      options: {
        label: t('pages.history.totalRequests'),
      },
    },
  },
  {
    key: 'generatedAtUtc',
    title: t('pages.history.generatedAtUtc'),
    sortable: true,
    visible: true,
    formatter: (v: string) => (v ? format(new Date(v), 'dd.MM.yyyy - HH:mm:ss') : '-'),
    group: t('pages.history.fieldGroups.general'),
    filter: {
      type: 'datetime',
      group: t('pages.history.filterGroups.general'),
      options: {
        label: t('pages.history.generatedAtUtc'),
      },
    },
  },
];

const visibleColumns = useTableFields(columns);
const {
  data: items,
  loading: loadingTable,
} = useProvider<HydraCollection<HistoryUsageStatistic>>('/history_usage_statistics', providerParams);

const records = computed(() => unref(items)?.['hydra:member']);
const total = computed(() => unref(items)?.['hydra:totalItems']);
const filters = computed(() =>
  columns.flatMap((col): Filter[] => {
    if (!col.filter) return [];
    if (Array.isArray(col.filter)) {
      return col.filter.map((f) => ({
        key: col.key,
        ...f,
      }));
    }
    return [{ key: col.key, ...col.filter }];
  }));
</script>

<style lang="scss">
.table {
  @apply flex flex-col;
}
</style>
